import { useQuery, useMutation } from "@tanstack/react-query";
import { useUser, useClerk, useAuth } from "@clerk/react";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, Zap, LogOut, RefreshCw, Film } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { useGetCurrentUser, useUpdateUserPreferences, useUpdateQuoteVideoPreference } from "@workspace/api-client-react";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const CINEMATIC_VIDEO_MONTHLY_LIMIT = 10;

interface PlanInfo {
  plan: "free" | "pro";
  usageCount: number;
  usageLimit: number | null;
  nextBillingDate: string | null;
  isFoundingGrant?: boolean;
  audioSecondsUsed?: number;
  audioSecondsLimit?: number;
  imageGenerationsUsed?: number;
  imageGenerationsLimit?: number;
  cinematicVideosUsed?: number;
}

function authHeaders(token: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

type VideoFormat = "portrait" | "landscape" | "square";
type VibeProfile = "grounded" | "cinematic" | "atmospheric";

const VIBE_OPTIONS: { value: VibeProfile; label: string; description: string }[] = [
  { value: "grounded", label: "Grounded", description: "Real-world, photographic look" },
  { value: "cinematic", label: "Cinematic", description: "Dramatic, film-quality imagery" },
  { value: "atmospheric", label: "Atmospheric", description: "Moody, evocative scenes" },
];

const FORMAT_OPTIONS: { value: VideoFormat; label: string; ratio: string }[] = [
  { value: "portrait", label: "Portrait", ratio: "9:16" },
  { value: "landscape", label: "Landscape", ratio: "16:9" },
  { value: "square", label: "Square", ratio: "1:1" },
];

export default function BillingPage() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useAuth();
  const { toast } = useToast();

  const searchParams = new URLSearchParams(window.location.search);
  const isCheckoutReturn = searchParams.get("success") === "1";
  const isPortalReturn = searchParams.get("portal") === "1";
  const isSyncReturn = isCheckoutReturn || isPortalReturn;
  const [isSyncing, setIsSyncing] = useState(isSyncReturn);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState(0);

  const { data: currentUser } = useGetCurrentUser();
  const updatePreferencesMutation = useUpdateUserPreferences();
  const updateQuoteVideoPrefMutation = useUpdateQuoteVideoPreference();

  const [preferredVibe, setPreferredVibe] = useState<VibeProfile | null>(null);
  const [preferredFormat, setPreferredFormat] = useState<VideoFormat>("portrait");
  const [prefSaved, setPrefSaved] = useState(false);
  const [autoQuoteVideos, setAutoQuoteVideos] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    if (currentUser.preferredVibeProfile) {
      setPreferredVibe(currentUser.preferredVibeProfile as VibeProfile);
    }
    if (currentUser.preferredVideoFormat) {
      setPreferredFormat(currentUser.preferredVideoFormat as VideoFormat);
    }
    setAutoQuoteVideos(currentUser.autoQuoteVideos ?? false);
  }, [currentUser]);

  const handleToggleAutoQuoteVideos = () => {
    const next = !autoQuoteVideos;
    setAutoQuoteVideos(next);
    updateQuoteVideoPrefMutation.mutate(
      { data: { autoQuoteVideos: next } },
      {
        onError: () => {
          setAutoQuoteVideos(!next);
          toast({ title: "Failed to update preference", variant: "destructive" });
        },
      },
    );
  };

  const handleSaveVideoDefaults = () => {
    updatePreferencesMutation.mutate(
      { data: { preferredVibeProfile: preferredVibe ?? null, preferredVideoFormat: preferredFormat } },
      {
        onSuccess: () => {
          setPrefSaved(true);
          toast({ title: "Video defaults saved" });
          setTimeout(() => setPrefSaved(false), 2000);
        },
        onError: () => {
          toast({ title: "Failed to save preferences", variant: "destructive" });
        },
      },
    );
  };

  const { data: plan, isLoading, refetch, isFetching } = useQuery<PlanInfo>({
    queryKey: ["plan"],
    queryFn: async () => {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/stripe/plan`, {
        credentials: "include",
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error("Failed to fetch plan");
      const data = await res.json();
      setLastUpdated(new Date());
      setSecondsAgo(0);
      return data;
    },
    enabled: !isSyncing,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (!lastUpdated) return;
    const interval = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastUpdated.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  useEffect(() => {
    if (!isSyncReturn) return;

    let cancelled = false;
    async function syncOnReturn() {
      try {
        const token = await getToken();
        const res = await fetch(`${basePath}/api/stripe/sync`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...authHeaders(token) },
        });
        if (!res.ok) throw new Error("Sync failed");
      } catch {
        // Sync failed — fall through to normal plan fetch
      } finally {
        if (!cancelled) {
          setIsSyncing(false);
          void refetch();
          const url = new URL(window.location.href);
          url.searchParams.delete("success");
          url.searchParams.delete("portal");
          window.history.replaceState({}, "", url.toString());
        }
      }
    }
    void syncOnReturn();
    return () => { cancelled = true; };
  }, []);

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/stripe/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create checkout");
      }
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const portalMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/stripe/portal`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to open portal");
      }
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Account & Billing</h1>
        <p className="text-muted-foreground mt-1">Manage your plan and subscription</p>
      </div>

      {/* User info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between">
          <div>
            <p className="font-medium text-foreground">{user?.fullName ?? user?.username ?? "—"}</p>
            <p className="text-sm text-muted-foreground">
              {user?.primaryEmailAddress?.emailAddress ?? ""}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => signOut({ redirectUrl: `${basePath}/` })}
          >
            <LogOut size={15} className="mr-2" />
            Sign out
          </Button>
        </CardContent>
      </Card>

      {/* Plan */}
      {isSyncing || isLoading ? (
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <Loader2 className="animate-spin text-muted-foreground" size={24} />
          {isSyncing && (
            <p className="text-sm text-muted-foreground">
              {isPortalReturn ? "Updating your subscription…" : "Activating your Pro plan…"}
            </p>
          )}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Current Plan</CardTitle>
              <div className="flex items-center gap-2">
                {lastUpdated && (
                  <span className="text-xs text-muted-foreground">
                    Last updated {secondsAgo < 5 ? "just now" : `${secondsAgo}s ago`}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => void refetch()}
                  disabled={isFetching}
                  className="h-5 w-5 text-muted-foreground hover:text-foreground"
                  aria-label="Refresh billing info"
                  title="Refresh now"
                >
                  <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />
                </Button>
                <Badge
                  variant={plan?.plan === "pro" ? "default" : "secondary"}
                  className={plan?.plan === "pro" ? "bg-indigo-500" : ""}
                >
                  {plan?.plan === "pro" ? "Pro" : "Free"}
                </Badge>
              </div>
            </div>
            <CardDescription>
              {plan?.plan === "pro"
                ? "Unlimited abbreviations, up to 90-minute episodes"
                : "5 episodes/month, up to 60-minute episodes"}
            </CardDescription>
            {plan?.plan === "pro" && plan.isFoundingGrant && plan.nextBillingDate && (
              <p className="text-xs text-muted-foreground pt-1">
                Founding program —{" "}
                <button
                  onClick={() => checkoutMutation.mutate()}
                  className="underline underline-offset-2 hover:text-foreground transition-colors"
                  disabled={checkoutMutation.isPending}
                >
                  Pro access expires on{" "}
                  {new Date(plan.nextBillingDate).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </button>
              </p>
            )}
            {plan?.plan === "pro" && !plan.isFoundingGrant && plan.nextBillingDate && (
              <p className="text-xs text-muted-foreground pt-1">
                Next billing date:{" "}
                {new Date(plan.nextBillingDate).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {plan?.plan === "free" && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Usage this month</span>
                  <span className="font-medium text-foreground">
                    {plan.usageCount} / {plan.usageLimit}
                  </span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 transition-all"
                    style={{ width: `${Math.min(100, ((plan.usageCount ?? 0) / (plan.usageLimit ?? 5)) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {plan?.plan === "pro" && plan.cinematicVideosUsed !== undefined && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Cinematic videos this month</span>
                  <span className={`font-medium ${plan.cinematicVideosUsed >= CINEMATIC_VIDEO_MONTHLY_LIMIT ? "text-red-500" : "text-foreground"}`}>
                    {plan.cinematicVideosUsed} / {CINEMATIC_VIDEO_MONTHLY_LIMIT}
                  </span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${plan.cinematicVideosUsed >= CINEMATIC_VIDEO_MONTHLY_LIMIT ? "bg-red-500" : "bg-indigo-500"}`}
                    style={{ width: `${Math.min(100, (plan.cinematicVideosUsed / CINEMATIC_VIDEO_MONTHLY_LIMIT) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {plan?.plan === "pro" && plan.audioSecondsUsed !== undefined && plan.audioSecondsLimit !== undefined && (
              <div className="space-y-2">
                {(() => {
                  const hoursUsed = plan.audioSecondsUsed / 3600;
                  const hoursLimit = plan.audioSecondsLimit / 3600;
                  const isRed = hoursUsed >= hoursLimit * 0.9;
                  return (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Audio hours this month</span>
                        <span className={`font-medium ${isRed ? "text-red-500" : "text-foreground"}`}>
                          {hoursUsed.toFixed(1)} / {Number.isInteger(hoursLimit) ? hoursLimit : hoursLimit.toFixed(1)}
                        </span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${isRed ? "bg-red-500" : "bg-indigo-500"}`}
                          style={{ width: `${Math.min(100, (hoursUsed / hoursLimit) * 100)}%` }}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {plan?.plan === "pro" && plan.imageGenerationsUsed !== undefined && plan.imageGenerationsLimit !== undefined && (
              <div className="space-y-2">
                {(() => {
                  const atLimit = plan.imageGenerationsUsed >= plan.imageGenerationsLimit;
                  const nearLimit = plan.imageGenerationsUsed >= plan.imageGenerationsLimit * 0.9;
                  const isRed = atLimit || nearLimit;
                  return (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">AI artwork generations this month</span>
                        <span className={`font-medium ${isRed ? "text-red-500" : "text-foreground"}`}>
                          {plan.imageGenerationsUsed} / {plan.imageGenerationsLimit}
                        </span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${isRed ? "bg-red-500" : "bg-indigo-500"}`}
                          style={{ width: `${Math.min(100, (plan.imageGenerationsUsed / plan.imageGenerationsLimit) * 100)}%` }}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {plan?.plan === "free" || plan?.isFoundingGrant ? (
              <div className="space-y-3">
                <div className="border border-border rounded-lg p-4 space-y-3">
                  <p className="font-semibold text-foreground">Upgrade to Pro — $19/month</p>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    {[
                      "Unlimited abbreviations",
                      "Episodes up to 90 minutes",
                      "All 3 abbreviation modes",
                      "Full job history",
                    ].map((feature) => (
                      <li key={feature} className="flex items-center gap-2">
                        <Check size={14} className="text-indigo-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full bg-indigo-500 hover:bg-indigo-600"
                    onClick={() => { trackEvent("upgrade_clicked"); checkoutMutation.mutate(); }}
                    disabled={checkoutMutation.isPending}
                  >
                    {checkoutMutation.isPending ? (
                      <Loader2 size={16} className="animate-spin mr-2" />
                    ) : (
                      <Zap size={16} className="mr-2" />
                    )}
                    Upgrade to Pro
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => portalMutation.mutate()}
                disabled={portalMutation.isPending}
              >
                {portalMutation.isPending ? (
                  <Loader2 size={16} className="animate-spin mr-2" />
                ) : null}
                Manage Subscription
              </Button>
            )}
          </CardContent>
        </Card>
      )}
      {/* Video defaults — Pro only */}
      {plan?.plan === "pro" && !isSyncing && !isLoading && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Film size={16} className="text-muted-foreground" />
              <CardTitle className="text-base">Video Defaults</CardTitle>
            </div>
            <CardDescription>
              These defaults apply to new cinematic videos. You can still change them per-episode.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Default format</p>
              <div className="flex gap-2 flex-wrap">
                {FORMAT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setPreferredFormat(opt.value)}
                    className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                      preferredFormat === opt.value
                        ? "border-indigo-500 bg-indigo-500/10 text-indigo-400"
                        : "border-border text-muted-foreground hover:border-foreground/40"
                    }`}
                  >
                    {opt.label}
                    <span className="text-xs ml-1 opacity-60">{opt.ratio}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Default vibe</p>
              <div className="flex flex-col gap-1.5">
                {VIBE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setPreferredVibe(opt.value)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md border text-left transition-colors ${
                      preferredVibe === opt.value
                        ? "border-indigo-500 bg-indigo-500/10"
                        : "border-border hover:border-foreground/40"
                    }`}
                  >
                    <span className="text-sm font-medium text-foreground">{opt.label}</span>
                    <span className="text-xs text-muted-foreground">{opt.description}</span>
                  </button>
                ))}
                <button
                  onClick={() => setPreferredVibe(null)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md border text-left transition-colors ${
                    preferredVibe === null
                      ? "border-indigo-500 bg-indigo-500/10"
                      : "border-border hover:border-foreground/40"
                  }`}
                >
                  <span className="text-sm font-medium text-foreground">None</span>
                  <span className="text-xs text-muted-foreground">Choose each time</span>
                </button>
              </div>
            </div>

            <Button
              className="w-full"
              onClick={handleSaveVideoDefaults}
              disabled={updatePreferencesMutation.isPending || prefSaved}
            >
              {updatePreferencesMutation.isPending ? (
                <Loader2 size={14} className="animate-spin mr-2" />
              ) : prefSaved ? (
                <Check size={14} className="mr-2" />
              ) : null}
              {prefSaved ? "Saved!" : "Save video defaults"}
            </Button>

            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Auto quote videos</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Automatically render a short video clip for each key quote on new RSS episodes.
                  </p>
                </div>
                <button
                  className={[
                    "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    autoQuoteVideos ? "bg-sky-500" : "bg-muted",
                  ].join(" ")}
                  role="switch"
                  aria-checked={autoQuoteVideos}
                  disabled={updateQuoteVideoPrefMutation.isPending}
                  onClick={handleToggleAutoQuoteVideos}
                >
                  <span
                    className={[
                      "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200",
                      autoQuoteVideos ? "translate-x-4" : "translate-x-0",
                    ].join(" ")}
                  />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
